// CreateSupplierDrawer.js
import React, { useMemo, useState } from "react"
import {
  Drawer,
  Form,
  Input,
  Select,
  Button,
  Space,
  message,
  Upload,
  Progress,
  Row,
  Col,
} from "antd"
import { PlusOutlined, InboxOutlined } from "@ant-design/icons"
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
} from "firebase/firestore"
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage"

const { TextArea } = Input
const { Dragger } = Upload

export default function CreateSupplierDrawer({
  onCreated,
  triggerLabel = "New Supplier",
  triggerIcon = <PlusOutlined />,
  buttonProps,
  dbInstance,
  storageInstance,
}) {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm()

  const [uploading, setUploading] = useState(false)
  const [uploadPct, setUploadPct] = useState(0)
  const [fileList, setFileList] = useState([])
  const [uploadedPath, setUploadedPath] = useState(null)

  const db = useMemo(() => dbInstance || getFirestore(), [dbInstance])
  const storage = useMemo(() => storageInstance || getStorage(), [storageInstance])

  const normalizeIban = (v) => (v ? v.replace(/\s+/g, "").toUpperCase() : v)
  const trimOrNull = (v) => (typeof v === "string" ? v.trim() : v || null)

  const handleSubmit = async (values) => {
    setSubmitting(true)
    try {
      const payload = {
        company: trimOrNull(values.company),
        name: trimOrNull(values.name),
        email: trimOrNull(values.email),
        phone: trimOrNull(values.phone),
        country: values.country || "Latvia",
        city: trimOrNull(values.city),
        deliveryCity: trimOrNull(values.deliveryCity),
        deliveryAddress: trimOrNull(values.deliveryAddress),
        deliveryPC: trimOrNull(values.deliveryPC),
        legalAddress: trimOrNull(values.legalAddress),
        legalCity: trimOrNull(values.legalCity),
        legalPC: trimOrNull(values.legalPC),
        postalCode: trimOrNull(values.postalCode),
        reg: trimOrNull(values.reg),
        vat: trimOrNull(values.vat),
        bankName: trimOrNull(values.bankName),
        bankCode: trimOrNull(values.bankCode),
        accountNumber: normalizeIban(values.accountNumber),
        logoUrl: trimOrNull(values.logoUrl),
        comment: trimOrNull(values.comment),
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }

      if (payload.reg) {
        const dupQ = query(collection(db, "suppliers"), where("reg", "==", payload.reg))
        const dupSnap = await getDocs(dupQ)
        if (!dupSnap.empty) {
          message.error("Supplier with this REG already exists.")
          setSubmitting(false)
          return
        }
      }

      const docRef = await addDoc(collection(db, "suppliers"), payload)
      message.success("Supplier created.")
      form.resetFields()
      setFileList([])
      setUploadedPath(null)
      setOpen(false)
      onCreated?.(docRef)
    } catch (e) {
      console.error(e)
      message.error("Failed to create supplier.")
    } finally {
      setSubmitting(false)
    }
  }

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/")
    if (!isImage) {
      message.error("Only image files are allowed.")
      return Upload.LIST_IGNORE
    }
    const isLt5M = file.size / 1024 / 1024 < 5
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB.")
      return Upload.LIST_IGNORE
    }
    return true
  }

  const customRequest = async ({ file, onProgress, onSuccess, onError }) => {
    try {
      setUploading(true)
      setUploadPct(0)

      if (uploadedPath) {
        try {
          await deleteObject(storageRef(storage, uploadedPath))
        } catch {}
      }

      const ext = file.name.includes(".") ? file.name.split(".").pop() : "jpg"
      const path = `suppliers/logos/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`

      const ref_ = storageRef(storage, path)
      const task = uploadBytesResumable(ref_, file)

      task.on(
        "state_changed",
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
          setUploadPct(pct)
          onProgress?.({ percent: pct })
        },
        (err) => {
          setUploading(false)
          onError?.(err)
          message.error("Upload failed.")
        },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref)
          setUploading(false)
          setUploadedPath(path)
          form.setFieldsValue({ logoUrl: url })
          onSuccess?.(url)
          message.success("Logo uploaded.")
        }
      )
    } catch (e) {
      setUploading(false)
      onError?.(e)
      message.error("Upload failed.")
    }
  }

  const handleRemove = async () => {
    try {
      if (uploadedPath) await deleteObject(storageRef(storage, uploadedPath))
    } catch {
    } finally {
      setUploadedPath(null)
      setFileList([])
      form.setFieldsValue({ logoUrl: null })
    }
    return true
  }

  return (
    <>
      <Button type="primary" icon={triggerIcon} onClick={() => setOpen(true)} {...buttonProps}>
        {triggerLabel}
      </Button>

      <Drawer
        title="Create Supplier"
        open={open}
        width={600}
        onClose={() => !submitting && setOpen(false)}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="primary" loading={submitting} onClick={() => form.submit()}>
              Create
            </Button>
          </Space>
        }>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ width: "100%" }}
          initialValues={{ country: "Latvia" }}>
          <Form.Item
            label="Company"
            name="company"
            rules={[{ required: true, message: "Company is required" }]}>
            <Input allowClear placeholder="e.g., DEPO SIA" />
          </Form.Item>

          <Form.Item label="Contact Person" name="name">
            <Input allowClear placeholder="Name Surname" />
          </Form.Item>

          {/* Email / Phone */}
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ type: "email", message: "Invalid email" }]}>
                <Input allowClear placeholder="contact@example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Phone" name="phone">
                <Input allowClear placeholder="+371 2XXXXXXX" />
              </Form.Item>
            </Col>
          </Row>

          {/* REG / VAT */}
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="REG" name="reg">
                <Input allowClear placeholder="4000XXXXXXX" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="VAT" name="vat">
                <Input allowClear placeholder="LV4000XXXXXXX" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Country" name="country">
            <Select
              options={[
                { value: "Latvia", label: "Latvia" },
                { value: "Lithuania", label: "Lithuania" },
                { value: "Estonia", label: "Estonia" },
                { value: "Poland", label: "Poland" },
                { value: "Other", label: "Other" },
              ]}
            />
          </Form.Item>

          <Form.Item label="Legal Address" name="legalAddress">
            <Input allowClear placeholder="Street, No." />
          </Form.Item>

          {/* Legal City / Legal PC */}
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Legal City" name="legalCity">
                <Input allowClear placeholder="Rīga" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Legal PC" name="legalPC">
                <Input allowClear placeholder="LV-1001" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Delivery Address" name="deliveryAddress">
            <Input allowClear placeholder="Street, No." />
          </Form.Item>

          {/* Delivery City / Delivery PC */}
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Delivery City" name="deliveryCity">
                <Input allowClear placeholder="Rīga" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Delivery PC" name="deliveryPC">
                <Input allowClear placeholder="LV-1001" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="(Optional) Postal Code (general)" name="postalCode">
            <Input allowClear placeholder="LV-XXXX" />
          </Form.Item>

          {/* Bank Name / Bank Code */}
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Bank Name" name="bankName">
                <Input allowClear placeholder='AS "Swedbank"' />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Bank Code (BIC/SWIFT)" name="bankCode">
                <Input allowClear placeholder="HABALV22" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Account (IBAN)" name="accountNumber">
            <Input allowClear placeholder="LV00BANK0000000000" />
          </Form.Item>

          {/* LOGO UPLOAD */}
          <Form.Item label="Logo">
            <Dragger
              multiple={false}
              accept="image/*"
              maxCount={1}
              beforeUpload={beforeUpload}
              customRequest={customRequest}
              onRemove={handleRemove}
              fileList={fileList}
              onChange={({ fileList: fl }) => setFileList(fl)}
              listType="picture">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag image to upload supplier logo</p>
              <p className="ant-upload-hint">PNG/JPG, up to 5MB.</p>
            </Dragger>
            {uploading ? <Progress percent={uploadPct} size="small" /> : null}
          </Form.Item>

          {/* Manual logo URL (autofilled after upload) */}
          <Form.Item label="Logo URL" name="logoUrl">
            <Input allowClear placeholder="https://…" />
          </Form.Item>

          <Form.Item label="Comment" name="comment">
            <TextArea allowClear rows={3} placeholder="Notes…" />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  )
}
